import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import DeleteConfirmMenuItem from "components/Shared/ConfirmModal/DeleteConfirmMenuItem";
import { assertPresence } from "utils/assertions";
import { removeDeletedRecords } from "utils/apollo/cacheHelpers";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

const fragment = gql(/* GraphQL */ `
  fragment DeleteSkuMenuItemSku on Sku {
    id
    name
    isArchived
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation DeleteSku($input: DeleteSkuInput!) {
    deleteSku(input: $input) {
      sku {
        id
        name
        productLine {
          id
        }
      }
    }
  }
`);

export type DeleteSkuData = DocumentType<typeof mutation>;

type Props = {
  onCompleted?: (data: DeleteSkuData) => void;
  sku: FragmentType<typeof fragment> | null | undefined;
};

export default function DeleteSkuMenuItem({
  onCompleted,
  sku: skuFragment,
}: Props) {
  const { showSuccessToast } = useAsyncCompleteToast();

  const sku = getFragmentData(fragment, skuFragment);

  const deleteMutation = useBrandMutation(mutation, {
    variables: sku ? { input: { id: sku.id } } : undefined,
    onCompleted: (data) => {
      onCompleted?.(data);
      const deletedSku = data?.deleteSku?.sku;
      assertPresence(deletedSku);
      showSuccessToast(`Deleted ${deletedSku.name}`, {
        status: "warning",
      });
    },
    update: (cache, result) => {
      const deletedSku = result.data?.deleteSku?.sku;
      assertPresence(deletedSku?.productLine);
      removeDeletedRecords({
        cache,
        baseFieldName: "skus",
        deletedRecords: [deletedSku],
      });
      removeDeletedRecords({
        cache,
        baseFieldName: "skus",
        deletedRecords: [deletedSku],
        typename: "ProductLine",
        id: deletedSku.productLine.id,
      });
    },
  });

  return (
    <DeleteConfirmMenuItem
      confirmText={
        <>
          Are you sure you want to delete the <b>{sku?.name}</b> SKU? You won’t
          be able to undo this action afterwards.
        </>
      }
      label="Permanently Delete SKU"
      mutation={deleteMutation}
      menuItemProps={{ isDisabled: !sku?.id || !sku?.isArchived }}
    />
  );
}
